import React from "react";
import {
  Col,
  Card,
  CardBody,
  CardHeader,
  Row,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
  Alert,
} from "reactstrap";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import GasFeeCalculator from "../GasFeeCalculator";

// AIDEV-NOTE: Main operation form component (Col-8) with proper NumericFormat usage
const MainOperationForm = ({
  // Form states
  cliente,
  direcao,
  moeda,
  cotacao,
  cotacaoFort,
  lucro,
  quantidade,
  ajustarPor,
  taxaFortPercentage,
  taxaRedeBrl,
  totalBrl,
  emitirNota,
  
  // Data lists
  listaClientes,
  listaMoedas,
  usdBrl,
  
  // Sub-operations
  subOperacoes,
  
  // Setters
  setCliente,
  setDirecao,
  setMoeda,
  setQuantidade,
  setAjustarPor,
  setTaxaFortPercentage,
  setTaxaRedeBrl,
  setTotalBrl,
  setEmitirNota,
}) => {
  return (
    <Col lg={8}>
      <Card>
        <CardHeader className="d-flex align-items-center border-0">
          <h5 className="card-title mb-0 flex-grow-1">
            Dados da Operação
          </h5>
          <Badge 
            color={direcao === "compra" ? "success" : "danger"}
            className="fs-6"
          >
            <FeatherIcon
              icon={direcao === "compra" ? "trending-up" : "trending-down"}
              className="me-1"
              size={14}
            />
            {direcao === "compra" ? "Compra" : "Venda"}
          </Badge>
        </CardHeader>
        <CardBody className="p-4">
          {/* Informações de referência */}
          <Row className="mb-4">
            <Col lg={6}>
              <GasFeeCalculator />
            </Col>
            <Col lg={6}>
              <Alert color="info">
                <strong>USDT / BRL:</strong> 
                <NumericFormat
                  value={usdBrl}
                  displayType="text"
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix=" R$ "
                />
              </Alert>
            </Col>
          </Row>

          <Row>
            {/* Cliente */}
            <Col md={6}>
              <FormGroup>
                <Label>Cliente *</Label>
                <Select
                  value={cliente}
                  onChange={setCliente}
                  options={listaClientes}
                  placeholder="Selecione um cliente..."
                  isSearchable
                  isClearable
                  required
                />
              </FormGroup>
            </Col>

            {/* Direção */}
            <Col md={6}>
              <FormGroup>
                <Label>Direção da Operação *</Label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="compra"
                      name="direcao"
                      value="compra"
                      checked={direcao === "compra"}
                      onChange={(e) => setDirecao(e.target.value)}
                      className="form-check-input"
                    />
                    <Label htmlFor="compra" className="form-check-label">
                      <FeatherIcon icon="trending-up" className="me-1" size={14} />
                      Compra
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="venda"
                      name="direcao"
                      value="venda"
                      checked={direcao === "venda"}
                      onChange={(e) => setDirecao(e.target.value)}
                      className="form-check-input"
                    />
                    <Label htmlFor="venda" className="form-check-label">
                      <FeatherIcon icon="trending-down" className="me-1" size={14} />
                      Venda
                    </Label>
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Moeda */}
            <Col md={6}>
              <FormGroup>
                <Label>Moeda *</Label>
                <Select
                  value={moeda}
                  onChange={setMoeda}
                  options={listaMoedas}
                  placeholder="Selecione uma moeda..."
                  isSearchable
                  isClearable
                  required
                />
              </FormGroup>
            </Col>

            {/* Quantidade (calculada ou manual) */}
            <Col md={6}>
              <FormGroup>
                <Label>
                  Quantidade 
                  {subOperacoes.length > 0 && (
                    <small className="text-muted"> (calculada pelas sub-operações)</small>
                  )}
                </Label>
                <NumericFormat
                  className="form-control bg-light"
                  value={quantidade}
                  displayType="text"
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={moeda?.data?.moeda === "BRL" ? 2 : 6}
                  fixedDecimalScale={true}
                  suffix={moeda?.data?.moeda !== "BRL" ? ` ${moeda?.data?.moeda || ""}` : ""}
                  prefix={moeda?.data?.moeda === "BRL" ? "R$ " : ""}
                />
                <small className="text-muted mt-1 d-block">
                  <i className="ri-information-line me-1"></i>
                  Calculado automaticamente pela soma das sub-operações
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Campos calculados */}
          <Row>
            {/* Cotação */}
            <Col md={6}>
              <FormGroup>
                <Label>
                  Cotação
                  <small className="text-muted">
                    {direcao === "compra" ? " (preço compra)" : " (preço conversão)"}
                  </small>
                </Label>
                <NumericFormat
                  className="form-control bg-light"
                  value={cotacao}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix="R$ "
                  allowNegative={false}
                  inputMode="decimal"
                  readOnly
                />
              </FormGroup>
            </Col>

            {/* Cotação Fort */}
            <Col md={6}>
              <FormGroup>
                <Label>Cotação Fort</Label>
                <NumericFormat
                  className="form-control bg-light"
                  value={cotacaoFort}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix="R$ "
                  allowNegative={false}
                  inputMode="decimal"
                  readOnly
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Toggle e campos dependentes */}
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>Ajustar por:</Label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="ajustar_taxa"
                      name="ajustar_por"
                      value="taxa"
                      checked={ajustarPor === "taxa"}
                      onChange={(e) => setAjustarPor(e.target.value)}
                      className="form-check-input"
                    />
                    <Label htmlFor="ajustar_taxa" className="form-check-label">
                      Taxa
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="ajustar_total"
                      name="ajustar_por"
                      value="total"
                      checked={ajustarPor === "total"}
                      onChange={(e) => setAjustarPor(e.target.value)}
                      className="form-check-input"
                    />
                    <Label htmlFor="ajustar_total" className="form-check-label">
                      Total
                    </Label>
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Taxa FORT (%) */}
            <Col md={6}>
              <FormGroup>
                <Label>Taxa FORT (%)</Label>
                <InputGroup>
                  <NumericFormat
                    className={`form-control ${ajustarPor === "total" ? "bg-light" : ""}`}
                    value={taxaFortPercentage}
                    onValueChange={({ value }) => {
                      setTaxaFortPercentage(value);
                    }}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={3}
                    fixedDecimalScale={false}
                    allowNegative={false}
                    placeholder="1,450"
                    disabled={ajustarPor === "total"}
                  />
                  <InputGroupText>%</InputGroupText>
                </InputGroup>
              </FormGroup>
            </Col>

            {/* Total BRL */}
            <Col md={6}>
              <FormGroup>
                <Label>Total BRL</Label>
                <NumericFormat
                  className={`form-control ${ajustarPor === "taxa" ? "bg-light" : ""}`}
                  value={totalBrl}
                  onValueChange={({ value }) => {
                    setTotalBrl(value);
                  }}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={false}
                  allowNegative={false}
                  placeholder="0,00"
                  prefix="R$ "
                  disabled={ajustarPor === "taxa"}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Lucro */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Lucro</Label>
                <NumericFormat
                  className="form-control bg-light"
                  value={lucro}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix="R$ "
                  allowNegative={false}
                  inputMode="decimal"
                  readOnly
                />
              </FormGroup>
            </Col>
            
            {/* Taxa Rede BRL */}
            <Col md={6}>
              <FormGroup>
                <Label>Taxa da Rede (BRL) *</Label>
                <NumericFormat
                  className="form-control"
                  value={taxaRedeBrl}
                  onValueChange={({ value }) => {
                    setTaxaRedeBrl(value || "0");
                  }}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale={false}
                  allowNegative={false}
                  placeholder="0,00"
                  prefix="R$ "
                />
                <small className="text-muted">
                  Taxa cobrada pela rede blockchain em BRL
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Nota Fiscal */}
          <Row>
            <Col md={12}>
              <FormGroup>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="emitir_nota"
                    className="form-check-input"
                    checked={emitirNota}
                    onChange={(e) => setEmitirNota(e.target.checked)}
                  />
                  <Label htmlFor="emitir_nota" className="form-check-label">
                    Emitir Nota contra o cliente?
                  </Label>
                </div>
                <small className="text-muted">
                  Marque esta opção se deseja emitir nota fiscal para esta operação
                </small>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

export default MainOperationForm; 